import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from "./context/ThemeContext";
import { ToastProvider } from "./context/ToastContext";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import Header from "./components/Header/Header";

import { createGlobalStyle } from 'styled-components';

import AppRouter from "./router/AppRouter";
import Loader from "./components/Loader/Loader";
import { AuthProvider, useAuth } from './context/AuthContext';
import { AuthModalProvider } from './context/AuthModalContext';
import { useTheme } from "./context/ThemeContext";
import { Toaster } from "react-hot-toast";
import DeletionGuard from "./components/DeletionGuard/DeletionGuard";
import { useNavigationGuard } from "./hooks/useNavigationGuard";
import { SettingsProvider } from "./hooks/useSettings";
import { useLanguageSync } from "./hooks/useLanguageSync";
import { LoadingProvider, useLoading } from "./context/LoadingContext";

const Footer = lazy(() => import("./components/Footer/Footer"));
const CookieConsent = lazy(() => import("./components/CookieConsent/CookieConsent"));
const SmartAppBanner = lazy(() => import("./components/SmartAppBanner/SmartAppBanner"));
const AnnouncementModal = lazy(() => import("./components/AnnouncementModal/AnnouncementModal"));
const ProfilePage = lazy(() => import('./pages/ProfilePage/ProfilePage'));


const GlobalStyle = createGlobalStyle`
  /* Загальне правило box-sizing для всіх елементів */
  *, *::before, *::after {
    box-sizing: border-box;
  }

  :root {
    --background-primary: ${props => props.theme === 'dark' ? '#121212' : '#fff7ec'};
    --background-secondary: ${props => props.theme === 'dark' ? '#1e1e1e' : '#ffffff'};
    --text-primary: ${props => props.theme === 'dark' ? '#ffffff' : '#333333'};
    --text-secondary: ${props => props.theme === 'dark' ? '#b3b3b3' : '#666666'};
    --accent-color: #ff6f00;
    --accent-hover: #ff8f00;
    --card-shadow: ${props => props.theme === 'dark' 
      ? '0 4px 6px rgba(0, 0, 0, 0.5)' 
      : '0 4px 6px rgba(0, 0, 0, 0.1)'};
  }

  html, body {
    margin: 0;
    padding: 0;
    min-height: 100%;
  }

  .app {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    width: 100%;
  }

  main {
    flex: 1 0 auto;
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - 70px);
    width: 100%;
  }

  body {
    background-color: var(--background-primary);
    color: var(--text-primary);
    transition: background-color 0.3s ease, color 0.3s ease;
  }

  
`;


const DeferredOverlays = () => {
  const [show, setShow] = React.useState(false);

  React.useEffect(() => {
    // Mount mobile app banner and overlays after initial critical paint
    const timer = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  if (!show) return null;

  return (
    <Suspense fallback={null}>
      <SmartAppBanner />
      <CookieConsent />
      <AnnouncementModal />
    </Suspense>
  );
};

const AppContent = () => {
  const location = useLocation();
  const { theme } = useTheme();
  const { user } = useAuth();
  const { isHomePageLoading } = useLoading();
  const isAdminPage = location.pathname.includes('/admin');
  const isHomePage = location.pathname === '/' || location.pathname.match(/^\/[a-z]{2}$/);
  // Перевіряємо, чи це сторінка чату підтримки або тікета
  // Приховуємо хедер та футер на цих сторінках для кращого UX
  const isSupportChat = location.pathname.startsWith('/support/chat/') || 
                        location.pathname.includes('/support/ticket/') ||
                        location.pathname.match(/^\/[a-z]{2}\/support\/ticket\//);
  

  
  // Додаємо navigation guard
  useNavigationGuard();

  // Синхронізуємо мову з URL
  const { currentLanguage } = useLanguageSync();

  return (
    <ThemeProvider>
      <DeletionGuard>
        <div className={`app ${theme}`}>
          {!isAdminPage && !isSupportChat && <Header />}
          <main role="main" aria-label={currentLanguage === 'uk' ? 'Основний вміст' : 'Main content'}>
            <Suspense fallback={<Loader />}>
              <Routes>
                <Route 
                  path="/profile" 
                  element={
                    user?.userHandle 
                      ? <Navigate to={`/profile/${user.userHandle}`} replace /> 
                      : <Navigate to="/" replace />
                  } 
                />
                <Route path="/profile/:userHandle?" element={<ProfilePage />} />
                <Route path="/*" element={<AppRouter />} />
              </Routes>
            </Suspense>
          </main>
          {!isAdminPage && !isSupportChat && (
            <Suspense fallback={null}>
              <Footer />
            </Suspense>
          )}
          <DeferredOverlays />
          <Toaster />
        </div>
      </DeletionGuard>
    </ThemeProvider>
  );
};

const App = () => {
  return (
    <HelmetProvider>
      <Router>
          <AuthProvider>
            <ThemeProvider>
              <LanguageProvider>
                <ToastProvider>
                  <AuthModalProvider>
                    <SettingsProvider>
                      <LoadingProvider>
                        <GlobalStyle />
                        <AppContent />
                      </LoadingProvider>
                    </SettingsProvider>
                  </AuthModalProvider>
                </ToastProvider>
              </LanguageProvider>
            </ThemeProvider>
          </AuthProvider>
      </Router>
    </HelmetProvider>
  );
};

export default App;
